/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1671667200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Thu Dec 22 2022 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "courier",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported sync methods",
          "depth": 0,
          "id": "supported-sync-methods"
        }, {
          "value": "Courier profiles",
          "depth": 1,
          "id": "courier-profiles"
        }, {
          "value": "Creating a new list",
          "depth": 1,
          "id": "creating-a-new-list"
        }, {
          "value": "Syncing to existing lists",
          "depth": 1,
          "id": "syncing-to-existing-lists"
        }, {
          "value": "Getting started",
          "depth": 0,
          "id": "getting-started"
        }, {
          "value": "Connect to Courier",
          "depth": 1,
          "id": "connect-to-courier"
        }, {
          "value": "Connect to your data source",
          "depth": 1,
          "id": "connect-to-your-data-source"
        }, {
          "value": "Create a model",
          "depth": 1,
          "id": "create-a-model"
        }, {
          "value": "Create a sync to Courier Profiles",
          "depth": 1,
          "id": "create-a-sync-to-courier-profiles"
        }, {
          "value": "Create a sync to Courier Lists",
          "depth": 1,
          "id": "create-a-sync-to-courier-lists"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      img: "img",
      h3: "h3",
      h4: "h4",
      ul: "ul",
      li: "li",
      code: "code",
      a: "a",
      ol: "ol",
      strong: "strong",
      pre: "pre",
      span: "span"
    }, _provideComponents(), props.components), {CommonErrorsPreface, PylonLink, LiveDebugger, SyncAlerts} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!PylonLink) _missingMdxReference("PylonLink", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-sync-methods",
        children: "Supported sync methods"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch can sync data to Courier Profiles and List Subscriptions"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-courier-sync-objects.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "courier-profiles",
        children: "Courier profiles"
      }), "\n", _jsx(_components.h4, {
        id: "sync-modes",
        children: "Sync modes"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports Upsert and Update sync modes:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "In the Upsert mode, new rows will be inserted into Courier and Profile attributes will be kept up-to-date within Courier"
        }), "\n", _jsx(_components.li, {
          children: "In the Update mode, Profile attributes will be kept up-to-date within Courier."
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["Database records can be matched from your source to your Courier workspace by a unique property. Profiles can be matched by a ", _jsx(_components.code, {
          children: "Recipient ID"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync columns from your source to Courier's Profile default and custom fields."
      }), "\n", _jsx(_components.h4, {
        id: "delete-mode",
        children: "Delete mode"
      }), "\n", _jsx(_components.p, {
        children: "You can choose what Hightouch's behavior is when records leave the query result set. The default is doing nothing, but you can also set Hightouch to delete or clear the Courier profile on record exit."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-courier-delete-mode.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "creating-a-new-list",
        children: "Creating a new list"
      }), "\n", _jsx(_components.p, {
        children: "In your sync configuration, you can tell Hightouch to create a new Courier list for you."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-courier-create-list.png",
          alt: ""
        })
      }), "\n", _jsx(_components.p, {
        children: "You can optionally assign a custom name to your list. If no name is provided, Hightouch will default to using your model name."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-courier-create-list-name.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "syncing-to-existing-lists",
        children: "Syncing to existing lists"
      }), "\n", _jsx(_components.p, {
        children: "Instead of creating a new list, Hightouch can also sync recipients to existing lists that are already populated in your Courier workspace."
      }), "\n", _jsxs(_components.p, {
        children: ["Suppose you want to sync to multiple existing lists but do not want to create a new sync for every list. As long as your model has a ", _jsx(_components.code, {
          children: "list_id"
        }), " column associated to a row, Hightouch can sync to multiple Courier lists in just one sync."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-courier-multiple-existing-list.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h2, {
        id: "getting-started",
        children: "Getting started"
      }), "\n", _jsxs(_components.p, {
        children: ["If you're new to Hightouch, be sure to ", _jsx(_components.a, {
          href: "https://app.hightouch.com/signup",
          children: "sign up for a free account"
        }), ". Your first destination is free, and Courier is a great way to get started."]
      }), "\n", _jsxs(_components.p, {
        children: ["Before diving into this tutorial, we also recommend reading ", _jsx(_components.a, {
          href: "/getting-started/concepts",
          children: "this article on our core concepts"
        }), ". It's a quick overview of how Hightouch works."]
      }), "\n", _jsx(_components.h3, {
        id: "connect-to-courier",
        children: "Connect to Courier"
      }), "\n", _jsx(_components.h4, {
        id: "create-an-api-token",
        children: "Create an API token"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Log in to Courier"
        }), "\n", _jsxs(_components.li, {
          children: ["Visit your ", _jsx(_components.strong, {
            children: "Settings"
          }), " page"]
        }), "\n", _jsxs(_components.li, {
          children: ["Click on the ", _jsx(_components.strong, {
            children: "API Keys"
          }), " tab"]
        }), "\n", _jsxs(_components.li, {
          children: ["If you already have an API key set up, copy the ", _jsx(_components.strong, {
            children: "Published"
          }), " token, otherwise click ", _jsx(_components.strong, {
            children: "Generate New API Key"
          })]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-courier-apikeys.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h4, {
        id: "add-courier-as-a-destination-in-hightouch",
        children: "Add Courier as a destination in Hightouch"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Log in to Hightouch or create a free Hightouch account"
        }), "\n", _jsxs(_components.li, {
          children: ["Click on ", _jsx(_components.strong, {
            children: "Destinations"
          }), " in the left sidebar"]
        }), "\n", _jsxs(_components.li, {
          children: ["Click on ", _jsx(_components.strong, {
            children: "Add destination"
          }), " in the top right corner"]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Courier"
          }), " in the destination catalog and click ", _jsx(_components.strong, {
            children: "Continue"
          }), " to proceed"]
        }), "\n", _jsxs(_components.li, {
          children: ["Paste your API token into the ", _jsx(_components.strong, {
            children: "API Key"
          }), " field and click ", _jsx(_components.strong, {
            children: "Continue"
          }), " to proceed"]
        }), "\n", _jsxs(_components.li, {
          children: ["Give your destination a name and unique slug (for example, “Courier Production” and ", _jsx(_components.code, {
            children: "courier-production"
          }), ")"]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Finish"
          }), " to create your Courier destination."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Now that we've linked Hightouch to your Courier account, it's time to connect to build profiles and list subscriptions using the data that resides in your data warehouse."
      }), "\n", _jsx(_components.h3, {
        id: "connect-to-your-data-source",
        children: "Connect to your data source"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch sits on top of your data warehouse and can read from Snowflake, Redshift, BigQuery, Databricks, Postgres, MySQL, and many other data sources. You can even fetch data from Google Sheets and Airtable. Hightouch can help activate your data wherever it resides."
      }), "\n", _jsx(_components.p, {
        children: "We've written dedicated guides for each supported data source. Take your pick:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/airtable",
            children: "Airtable"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/amazon-athena",
            children: "Amazon Athena"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/amazon-redshift",
            children: "Amazon Redshift"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/clickhouse",
            children: "ClickHouse"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/databricks",
            children: "Databricks"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/elasticsearch",
            children: "Elasticsearch"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/firebolt",
            children: "Firebolt"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/google-bigquery",
            children: "Google BigQuery"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/google-sheets",
            children: "Google Sheets"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/looker",
            children: "Looker"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/metabase",
            children: "Metabase"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/mysql",
            children: "MySQL"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/foundry",
            children: "Palantir Foundry"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/postgresql",
            children: "PostgreSQL"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/rockset",
            children: "Rockset"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/sftp",
            children: "SFTP"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/snowflake",
            children: "Snowflake"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/sqlserver",
            children: "SQL Server"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/trino",
            children: "Trino"
          })]
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "If you're missing the credentials necessary to connect to your data source,\ntry inviting your teammates to Hightouch. You can have unlimited collaborators\nin your workspace."
        })
      }), "\n", _jsx(_components.h4, {
        id: "connect-to-a-sample-database-instead",
        children: "Connect to a sample database instead"
      }), "\n", _jsxs(_components.p, {
        children: ["Waiting for credentials to your data warehouse? No problem. You can connect to one of our sample databases to get started right away. For the rest of this tutorial, we'll use our demo Postgres database, which contains a ", _jsx(_components.code, {
          children: "users"
        }), " table with names, emails, locations, and other user attributes. This sample source is available when you log in for the first time—no setup required."]
      }), "\n", _jsx(_components.p, {
        children: "Next, we'll write a SQL query to define which profiles and list subscriptions should be synced to Courier."
      }), "\n", _jsx(_components.h3, {
        id: "create-a-model",
        children: "Create a model"
      }), "\n", _jsx(_components.p, {
        children: "In Hightouch, a model represents a query that filters or transforms the records in your data source. Models are used to determine exactly which records to sync between sources and destinations."
      }), "\n", _jsx(_components.p, {
        children: "When you connect to a data warehouse, your models will probably be written in SQL. You can also import models from tools like dbt and Looker."
      }), "\n", _jsx(_components.p, {
        children: "In this example, we'll keep it simple. Suppose you want to use Courier to send notifications only for your customers located in the city of Nashville. To accomplish this, you'll want to create a model that looks like this:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-sql",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "SELECT"
          }), " ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "*"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "FROM"
          }), " users ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "WHERE"
          }), " location ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'Nashville'"
          }), ";\n"]
        })
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Click on ", _jsx(_components.strong, {
            children: "Models"
          }), " in the left sidebar"]
        }), "\n", _jsxs(_components.li, {
          children: ["Click on ", _jsx(_components.strong, {
            children: "Add model"
          }), " in the top right corner"]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Demo Database"
          }), " as your source and click ", _jsx(_components.strong, {
            children: "Continue"
          }), " to proceed"]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "SQL Editor"
          }), " as your modeling method"]
        }), "\n", _jsx(_components.li, {
          children: "Paste the SQL query from above into the editor on the left side of the page"
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Preview"
          }), " to execute the query"]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Continue"
          }), " to proceed"]
        }), "\n", _jsxs(_components.li, {
          children: ["Give your model a name and unique slug (for example, \"Users in Nashville\" and ", _jsx(_components.code, {
            children: "users-in-nashville"
          }), ")"]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "ID"
          }), " as the primary key for your model"]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Finish"
          }), " to create your model."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["We just created a model using a SQL query that filters our ", _jsx(_components.code, {
          children: "users"
        }), " table down to only those located in Nashville. Next, we'll configure our sync to add these users to Courier."]
      }), "\n", _jsx(_components.h3, {
        id: "create-a-sync-to-courier-profiles",
        children: "Create a sync to Courier Profiles"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Click on ", _jsx(_components.strong, {
            children: "Syncs"
          }), " in the left sidebar"]
        }), "\n", _jsxs(_components.li, {
          children: ["Click on ", _jsx(_components.strong, {
            children: "Add sync"
          }), " in the top right corner"]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Users in Nashville"
          }), " as your model and click ", _jsx(_components.strong, {
            children: "Continue"
          }), " to proceed"]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Courier Production"
          }), " as your destination and click ", _jsx(_components.strong, {
            children: "Continue"
          }), " to proceed"]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Profile"
          }), " as the object you wish to update in Courier"]
        }), "\n", _jsx(_components.li, {
          children: "Select which update mode you want to use"
        }), "\n", _jsx(_components.li, {
          children: "Identify which field from your model represents the identifier known to Courier"
        }), "\n", _jsx(_components.li, {
          children: "Identify which fields from your model you want to sync to a Courier Profile"
        }), "\n", _jsx(_components.li, {
          children: "Select how you want to handle records leaving your source"
        }), "\n", _jsx(_components.li, {
          children: "Give a name to your segment (for example, \"My beta test\")"
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Continue"
          }), " to proceed to the last step"]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Interval"
          }), " to tell your sync to run on a set interval"]
        }), "\n", _jsxs(_components.li, {
          children: ["Configure your sync schedule to run every ", _jsx(_components.strong, {
            children: "5 minutes"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Finish"
          }), " to create your sync."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Your Courier sync configuration would look something like this."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-courier-profiles-sync-config.png",
          alt: ""
        })
      }), "\n", _jsx(_components.p, {
        children: "And your sync schedule configuration would resemble this."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-launchdarkly-create-sync-schedule-config.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "create-a-sync-to-courier-lists",
        children: "Create a sync to Courier Lists"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Click on ", _jsx(_components.strong, {
            children: "Syncs"
          }), " in the left sidebar"]
        }), "\n", _jsxs(_components.li, {
          children: ["Click on ", _jsx(_components.strong, {
            children: "Add sync"
          }), " in the top right corner"]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Users in Nashville"
          }), " as your model and click ", _jsx(_components.strong, {
            children: "Continue"
          }), " to proceed"]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Courier Production"
          }), " as your destination and click ", _jsx(_components.strong, {
            children: "Continue"
          }), " to proceed"]
        }), "\n", _jsx(_components.li, {
          children: "Identify which field from your model represents the identifier known to Courier"
        }), "\n", _jsx(_components.li, {
          children: "Select Create a new segment"
        }), "\n", _jsx(_components.li, {
          children: "Give a name to your segment (for example, \"My beta test\")"
        }), "\n", _jsx(_components.li, {
          children: "Click Continue to proceed to the last step"
        }), "\n", _jsx(_components.li, {
          children: "Select Interval to tell your sync to run on a set interval"
        }), "\n", _jsx(_components.li, {
          children: "Configure your sync schedule to run every 5 minutes"
        }), "\n", _jsx(_components.li, {
          children: "Click Finish to create your sync."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Your Courier sync configuration would look something like this."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-courier-lists-sync-config.png",
          alt: ""
        })
      }), "\n", _jsx(_components.p, {
        children: "And your sync schedule configuration would resemble this."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-launchdarkly-create-sync-schedule-config.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsx(_components.h4, {
        id: "dont-see-your-data-in-courier",
        children: "Don't see your data in Courier?"
      }), "\n", _jsxs(_components.p, {
        children: ["Make sure you've configured your destination to use the correct Courier environment API key. There may be quite a few so you might've accidentally copied the wrong one. If that doesn't solve your issue, ", _jsx(PylonLink, {
          pylonText: "",
          linkText: "please reach out"
        }), ". We're happy to help."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-courier-troubleshoot-apikeys.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
